import React, { useEffect, useMemo, useRef, useState } from 'react';
import { gameState } from '../game/state/GameState';
import TileNode from './TileNodeV2';
import HologramViewport from './HologramViewport';
import BlindGuessPanel from './BlindGuessPanel';
import CollectionView from './CollectionView';
import Leaderboard from '../game/Leaderboard';
import RevealCeremony from './RevealCeremony';
import SliceGuideCube from './SliceGuideCube';
import {
    IconHub,
    IconKey,
    IconLock,
    IconMuseum,
    IconSquad,
    IconTag,
    IconWand,
} from './AtelierIcons';
import { getDailyModelId, getModelById } from '../game/voxel/models';
import { publicShellPayload, shellVoxelsForSlice, rankSlicesForSuggestion } from '../game/voxel/slicePipeline';
import {
    getEnergyState,
    spendEnergy,
    refundEnergyLocal,
    markContributed,
    hasContributedToday,
    unlockCollectionEntry,
    getGuessState,
} from '../game/meta/progression';
import { claimTile, fetchVault, fetchProgress, setPreferredTier } from '../game/actions/TileActions';
import { ensureDailyAssists, formatLockCountdown } from '../game/meta/atelierAssists';
import { canUseDebugTools } from '../config/game_config';
import { buildSyndicateInviteLink, shareSyndicateInvite } from '../config/telegramLaunch';
import { trackShare } from '../game/meta/analytics';
import {
    ATELIER_UNLOCK_HALF_CLEARS,
    ATELIER_UNLOCK_SLICES,
    MYSTERY_TIER,
    PUBLIC_ATELIER_ID,
    PUBLIC_WORKSHOP_ID,
    TIER_LABEL,
} from '../game/config/tiers';

const MysteryHub = ({ onFeedback, onOpenLobby }) => {
    const [tiles, setTiles] = useState([]);
    const [isComplete, setIsComplete] = useState(false);
    const [hasDismissed, setHasDismissed] = useState(false);
    const [showLeaderboard, setShowLeaderboard] = useState(false);
    const [showCollection, setShowCollection] = useState(false);
    const [energy, setEnergy] = useState(getEnergyState());
    const [sliceFocus, setSliceFocus] = useState(null);
    const [selectedTileId, setSelectedTileId] = useState(null);
    const [didAutoPick, setDidAutoPick] = useState(false);
    const [sliceFilter, setSliceFilter] = useState('OPEN'); // ALL | OPEN
    const [nowTick, setNowTick] = useState(() => Date.now());
    const [mysteryTier, setMysteryTierUi] = useState(gameState.mysteryTier || MYSTERY_TIER.WORKSHOP);
    const [atelierUnlocked, setAtelierUnlocked] = useState(!!gameState.atelierUnlocked);
    const [unlockProgress, setUnlockProgress] = useState({
        slices: gameState.workshopSlicesSolved || 0,
        halfClears: gameState.workshopHalfClears || 0,
    });
    const [showUnlockModal, setShowUnlockModal] = useState(false);
    const date = useMemo(() => new Date().toISOString().split('T')[0], []);
    const modelId = useMemo(
        () => getDailyModelId(date, mysteryTier),
        [date, mysteryTier]
    );
    const model = useMemo(() => getModelById(modelId), [modelId]);
    const dateLabel = useMemo(
        () =>
            new Date().toLocaleDateString(undefined, {
                month: 'short',
                day: 'numeric',
            }),
        []
    );

    const forceReveal = useMemo(
        () =>
            canUseDebugTools() &&
            new URLSearchParams(window.location.search).get('forceReveal') === '1',
        []
    );
    const hasDismissedRef = useRef(hasDismissed);
    hasDismissedRef.current = hasDismissed;

    useEffect(() => {
        ensureDailyAssists();
        fetchProgress().then((res) => {
            if (!res?.success) return;
            setAtelierUnlocked(!!gameState.atelierUnlocked);
            setMysteryTierUi(gameState.mysteryTier);
            setUnlockProgress({
                slices: gameState.workshopSlicesSolved || 0,
                halfClears: gameState.workshopHalfClears || 0,
            });
        });
        const sync = () => {
            const gridArray = Array.from(gameState.vault.getTiles());
            setTiles(gridArray);
            const vaultComplete = gameState.getCurrentState() === 'VAULT_COMPLETE';
            setIsComplete(vaultComplete || (forceReveal && !hasDismissedRef.current));
            if (vaultComplete) {
                /* keep dismissed as-is */
            } else if (!forceReveal) {
                setHasDismissed(false);
            }
            setEnergy(getEnergyState());
            setMysteryTierUi(gameState.mysteryTier);
            setAtelierUnlocked(!!gameState.atelierUnlocked);
            setUnlockProgress({
                slices: gameState.workshopSlicesSolved || 0,
                halfClears: gameState.workshopHalfClears || 0,
            });
            if (gameState.justUnlockedAtelier) {
                const seen = localStorage.getItem('vq_atelier_unlock_seen') === '1';
                if (!seen) {
                    setShowUnlockModal(true);
                    localStorage.setItem('vq_atelier_unlock_seen', '1');
                }
                gameState.clearUnlockToast();
            }
        };
        sync();
        const interval = setInterval(sync, 500);
        return () => clearInterval(interval);
    }, [forceReveal]);

    const handleTierSwitch = async (tier) => {
        if (tier === MYSTERY_TIER.ATELIER && !atelierUnlocked) {
            onFeedback?.({
                type: 'info',
                title: 'Atelier locked',
                body: `Solve ${ATELIER_UNLOCK_SLICES} Workshop slices or clear ${ATELIER_UNLOCK_HALF_CLEARS} Workshop mysteries to 50%.`,
            });
            return;
        }
        const ok = gameState.setMysteryTier(tier);
        if (!ok) return;
        setMysteryTierUi(tier);
        setDidAutoPick(false);
        setSelectedTileId(null);
        setPreferredTier(tier).catch(() => {});
        const res = await fetchVault();
        if (!res?.success) {
            onFeedback?.({ type: 'error', title: 'Could not load floor', body: res?.error });
        } else {
            onFeedback?.(
                tier === MYSTERY_TIER.WORKSHOP
                    ? 'Entered Workshop floor'
                    : 'Entered Atelier floor'
            );
        }
    };

    // removed duplicate forceReveal mount effect — handled in sync above

    const suggestionAvoid = useMemo(() => {
        const me = gameState.currentUser?.id;
        return tiles.filter(
            (t) =>
                t.status === 'SOLVED' ||
                (t.status === 'CLAIMED' && me && t.claimedBy === me)
        );
    }, [tiles]);

    useEffect(() => {
        if (didAutoPick || isComplete || tiles.length === 0 || !model) return;
        const open = tiles.filter((t) => t.status === 'OPEN' && t.data?.axis != null);
        if (!open.length) return;
        const [pick] = rankSlicesForSuggestion(open, model.size, { avoid: suggestionAvoid });
        setSelectedTileId(pick.id);
        setSliceFocus({ axis: pick.data.axis, index: pick.data.index });
        setDidAutoPick(true);
    }, [tiles, didAutoPick, isComplete, model, suggestionAvoid]);

    useEffect(() => {
        if (!selectedTileId || isComplete || !model) return;
        const current = tiles.find((t) => t.id === selectedTileId);
        if (current && current.status === 'OPEN') return;
        const open = tiles.filter((t) => t.status === 'OPEN' && t.data?.axis != null);
        if (!open.length) {
            setSelectedTileId(null);
            setSliceFocus(null);
            return;
        }
        const [pick] = rankSlicesForSuggestion(open, model.size, { avoid: suggestionAvoid });
        setSelectedTileId(pick.id);
        setSliceFocus({ axis: pick.data.axis, index: pick.data.index });
    }, [tiles, selectedTileId, isComplete, model, suggestionAvoid]);

    const revealedKeys = useMemo(() => {
        const keys = new Set();
        if (!model) return keys;
        tiles.forEach((t) => {
            if (t.status !== 'SOLVED') return;
            const axis = t.data?.axis;
            const index = t.data?.index;
            if (axis == null || index == null) return;
            shellVoxelsForSlice(model.volume, axis, index).forEach((v) => {
                keys.add(`${v.x},${v.y},${v.z}`);
            });
        });
        return keys;
    }, [tiles, model]);

    const hologram = useMemo(() => {
        if (!model) return { size: 16, voxels: [], palette: {} };
        const payload = publicShellPayload(model, isComplete ? null : revealedKeys);
        if (!isComplete) {
            payload.voxels = payload.voxels.map((v) => ({
                ...v,
                revealed: revealedKeys.has(`${v.x},${v.y},${v.z}`),
            }));
        } else {
            payload.voxels = payload.voxels.map((v) => ({ ...v, revealed: true }));
        }
        return payload;
    }, [model, revealedKeys, isComplete]);

    const solvedCount = tiles.filter((t) => t.status === 'SOLVED').length;
    const pct = tiles.length ? Math.round((solvedCount / tiles.length) * 100) : 0;

    const solvedPlanes = useMemo(
        () =>
            tiles
                .filter((t) => t.status === 'SOLVED' && t.data?.axis != null)
                .map((t) => ({ axis: t.data.axis, index: t.data.index })),
        [tiles]
    );

    const visibleTiles = useMemo(() => {
        if (sliceFilter === 'OPEN') return tiles.filter((t) => t.status === 'OPEN');
        return tiles;
    }, [tiles, sliceFilter]);

    useEffect(() => {
        // Unlock collection only after reveal ceremony dismiss (same for Workshop + Atelier).
        if (!isComplete || !hasDismissed || !model) return;
        const guess = getGuessState(date);
        if (hasContributedToday(date) || guess.correct) {
            unlockCollectionEntry({
                modelId: model.id,
                name: model.name,
                category: model.category,
                date,
                quality: gameState.mysteryTier === 'atelier' ? 'atelier' : 'standard',
            });
        }
    }, [isComplete, hasDismissed, date, model]);

    const handleClaimWithEnergy = async (tileId) => {
        const me = gameState.currentUser?.id;
        // Already holding a slice — open that nonogram instead of spending another key
        const held = me ? gameState.vault.findActiveLockForUser(me) : null;
        if (held) {
            gameState.selectTile(held.id);
            setEnergy(getEnergyState());
            onFeedback?.('Resume your claimed slice');
            return;
        }

        const before = getEnergyState();
        if (before.keys <= 0) {
            onFeedback?.({
                type: 'error',
                title: 'No energy keys left',
                body: 'Regen 1 key every 90m, or use Dev → Reset guesses',
            });
            return;
        }

        const spent = spendEnergy();
        setEnergy(getEnergyState());
        if (!spent.ok) {
            onFeedback?.({
                type: 'error',
                title: 'No energy keys left',
                body: 'Regen 1 key every 90m, or use Dev → Reset guesses',
            });
            return;
        }

        const res = await claimTile(tileId);
        if (!res.success) {
            // Don't soft-lock feel-test: refund key when claim fails
            refundEnergyLocal();
            setEnergy(getEnergyState());

            // Server may already hold a lock for us — resume that slice
            if (String(res.error || '').includes('USER_HAS_ACTIVE_LOCK')) {
                await fetchVault();
                const lock = me ? gameState.vault.findActiveLockForUser(me) : null;
                if (lock) {
                    gameState.selectTile(lock.id);
                    onFeedback?.('Opened your existing claimed slice');
                    return;
                }
            }

            onFeedback?.({
                type: 'error',
                title: 'Claim failed',
                body: String(res.error || 'Try another plane'),
            });
            return;
        }

        markContributed(date);
        gameState.selectTile(tileId);
        setEnergy(getEnergyState());
        onFeedback?.(`SLICE CLAIMED · Energy ${spent.keys}/3`);
    };

    const selectedTile = tiles.find((t) => t.id === selectedTileId) || null;
    const selectedLabel =
        selectedTile?.data?.axis != null
            ? `${String(selectedTile.data.axis).toUpperCase()}=${selectedTile.data.index}`
            : null;
    const heldSlice = (() => {
        const me = gameState.currentUser?.id;
        return me ? tiles.find((t) => t.status === 'CLAIMED' && t.claimedBy === me) : null;
    })();
    const heldLabel =
        heldSlice?.data?.axis != null
            ? `${String(heldSlice.data.axis).toUpperCase()}=${heldSlice.data.index}`
            : heldSlice?.id;
    const heldLock =
        heldSlice?.lockExpiry != null
            ? formatLockCountdown(heldSlice.lockExpiry, nowTick)
            : null;

    useEffect(() => {
        if (!heldSlice?.lockExpiry) return undefined;
        const id = setInterval(() => setNowTick(Date.now()), 500);
        return () => clearInterval(id);
    }, [heldSlice?.id, heldSlice?.lockExpiry]);

    const handleSelectSlice = (tile) => {
        if (tile.status === 'OPEN') {
            setSelectedTileId(tile.id);
            if (tile.data?.axis != null) {
                setSliceFocus({ axis: tile.data.axis, index: tile.data.index });
            }
            return;
        }
        if (tile.status === 'CLAIMED' && tile.claimedBy === gameState.currentUser?.id) {
            gameState.selectTile(tile.id);
        }
    };

    if (tiles.length === 0) {
        return (
            <div className="atelier-hub">
                <div className="atelier-tier-switch" role="group" aria-label="Mystery floor">
                    <button
                        type="button"
                        className={`atelier-tier-btn${
                            mysteryTier === MYSTERY_TIER.WORKSHOP ? ' is-active' : ''
                        }`}
                        onClick={() => handleTierSwitch(MYSTERY_TIER.WORKSHOP)}
                    >
                        Workshop
                        <span className="atelier-tier-meta">8³</span>
                    </button>
                    <button
                        type="button"
                        className={`atelier-tier-btn${
                            mysteryTier === MYSTERY_TIER.ATELIER ? ' is-active' : ''
                        }${atelierUnlocked ? '' : ' is-locked'}`}
                        onClick={() => handleTierSwitch(MYSTERY_TIER.ATELIER)}
                    >
                        Atelier
                        <span className="atelier-tier-meta">16³</span>
                    </button>
                </div>
                <div className="atelier-sync">Syncing mystery object…</div>
            </div>
        );
    }

    const categoryStamp = String(model?.category || 'mystery').toUpperCase();
    const floorStamp =
        mysteryTier === MYSTERY_TIER.ATELIER ? 'ATELIER' : 'WORKSHOP';

    return (
        <div className="atelier-hub">
            <div className="atelier-topbar">
                <div className="atelier-brand">
                    <span className="atelier-brand-mark" aria-hidden>
                        <IconWand />
                    </span>
                    <div>
                        <div className="atelier-brand-name">
                            Voxel {TIER_LABEL[mysteryTier] || 'Workshop'}
                        </div>
                        <div
                            className="atelier-brand-sub atelier-brand-sub--ellipsis"
                            title={
                                gameState.currentSquadName === 'Public Matchmaking'
                                    ? 'Public'
                                    : gameState.currentSquadName || 'Public'
                            }
                        >
                            {gameState.currentSquadName === 'Public Matchmaking'
                                ? 'Public'
                                : gameState.currentSquadName || 'Public'}
                        </div>
                        {gameState.currentSquadId &&
                            gameState.currentSquadId !== PUBLIC_ATELIER_ID &&
                            gameState.currentSquadId !== PUBLIC_WORKSHOP_ID &&
                            gameState.currentSquadId !== 'public-syndicate-0000' && (
                                <button
                                    type="button"
                                    className="atelier-chip-btn"
                                    style={{ marginTop: 6 }}
                                    onClick={async () => {
                                        const result = await shareSyndicateInvite(
                                            gameState.currentSquadId,
                                            gameState.currentSquadName
                                        );
                                        trackShare(result, {
                                            source: 'hub_invite',
                                            tier: gameState.mysteryTier || 'workshop',
                                        });
                                        if (result === 'shared') {
                                            onFeedback?.({
                                                type: 'success',
                                                title: 'Invite friends',
                                                body: 'Pick a Telegram chat — message includes the join link.',
                                            });
                                        } else if (result === 'copied') {
                                            onFeedback?.({
                                                type: 'success',
                                                title: 'Invite message copied',
                                                body: buildSyndicateInviteLink(
                                                    gameState.currentSquadId
                                                ),
                                            });
                                        } else {
                                            onFeedback?.({
                                                type: 'error',
                                                title: 'Could not share invite',
                                            });
                                        }
                                    }}
                                >
                                    Invite friends
                                </button>
                            )}
                    </div>
                </div>
                <div className="atelier-tier-switch" role="group" aria-label="Mystery floor">
                    <button
                        type="button"
                        className={`atelier-tier-btn${
                            mysteryTier === MYSTERY_TIER.WORKSHOP ? ' is-active' : ''
                        }`}
                        onClick={() => handleTierSwitch(MYSTERY_TIER.WORKSHOP)}
                    >
                        Workshop
                        <span className="atelier-tier-meta">8³</span>
                    </button>
                    <button
                        type="button"
                        className={`atelier-tier-btn${
                            mysteryTier === MYSTERY_TIER.ATELIER ? ' is-active' : ''
                        }${atelierUnlocked ? '' : ' is-locked'}`}
                        onClick={() => handleTierSwitch(MYSTERY_TIER.ATELIER)}
                        title={
                            atelierUnlocked
                                ? 'Atelier floor'
                                : `Unlock: ${unlockProgress.slices}/${ATELIER_UNLOCK_SLICES} slices or ${unlockProgress.halfClears}/${ATELIER_UNLOCK_HALF_CLEARS} half-clears`
                        }
                    >
                        Atelier
                        <span className="atelier-tier-meta">
                            {atelierUnlocked
                                ? '16³'
                                : `${Math.min(unlockProgress.slices, ATELIER_UNLOCK_SLICES)}/${ATELIER_UNLOCK_SLICES}`}
                        </span>
                    </button>
                </div>
                <div className="atelier-keys" title="Energy keys">
                    <button
                        type="button"
                        className="atelier-chip-btn"
                        onClick={() => {
                            setShowLeaderboard(true);
                            setShowCollection(false);
                        }}
                    >
                        Board
                    </button>
                    <span className="atelier-keys-icon" aria-hidden>
                        <IconKey />
                    </span>
                    <span>
                        {energy.keys}/3 Keys
                    </span>
                </div>
            </div>

            <section className="atelier-parchment" aria-label="Today's mystery">
                <div className="atelier-parchment-row">
                    <span className="atelier-stamp">
                        <IconTag className="atelier-inline-ico" />
                        [{categoryStamp} · {floorStamp}]
                    </span>
                    <span className="atelier-session">
                        {!isComplete && <IconLock className="atelier-inline-ico" />}
                        Workshop · {dateLabel}
                    </span>
                </div>
                <h2 className="atelier-mystery-title">
                    {isComplete ? model.name : "TODAY'S MYSTERY"}
                </h2>
                <div className="atelier-parchment-meta">
                    <span>
                        {isComplete
                            ? 'Restored by the syndicate'
                            : 'Category only — name unlocks at reveal'}
                    </span>
                    <span className="atelier-tier">TIER · ARTISAN</span>
                </div>
            </section>

            <section className="atelier-stage" aria-label="Reconstruction">
                <div className="atelier-stage-chrome">
                    <span className="atelier-chip">{tiles.length} SLICES</span>
                    <span className="atelier-chip atelier-chip--soft">{pct}% rebuilt</span>
                </div>

                {isComplete || pct > 0 ? (
                    <HologramViewport
                        size={hologram.size}
                        voxels={hologram.voxels}
                        palette={hologram.palette}
                        grayscale={!isComplete || !hasDismissed}
                        colorBlend={isComplete && hasDismissed ? 1 : 0}
                        autoRotate={isComplete && hasDismissed}
                        presentation={isComplete && hasDismissed ? 'trophy' : 'free'}
                    />
                ) : (
                    <div className="atelier-empty-holo" aria-label="Reconstruction empty">
                        <span className="atelier-empty-glyph">?</span>
                        <p>
                            Reconstruction appears after your first solved slice — claim a plane
                            below.
                        </p>
                    </div>
                )}

                <div className="atelier-progress">
                    <div className="atelier-progress-labels">
                        <span>{pct}% rebuilt together</span>
                        <span>
                            {solvedCount} / {tiles.length} slices
                        </span>
                    </div>
                    <div className="atelier-progress-track">
                        <div className="atelier-progress-fill" style={{ width: `${pct}%` }} />
                    </div>
                </div>
            </section>

            {!isComplete && heldSlice && (
                <div className="atelier-claim">
                    <div className="atelier-claim-copy">
                        <div className="atelier-claim-kicker">Your claimed plane</div>
                        <div className="atelier-claim-plane">{heldLabel}</div>
                        {heldLock && !heldLock.expired && (
                            <div
                                className={`atelier-claim-timer${
                                    heldLock.critical
                                        ? ' is-critical'
                                        : heldLock.urgent
                                          ? ' is-urgent'
                                          : ''
                                }`}
                            >
                                Lock {heldLock.label} left
                                {heldLock.urgent ? ' · use Lock Oil or finish soon' : ''}
                            </div>
                        )}
                    </div>
                    <button
                        className="atelier-btn-primary atelier-claim-btn"
                        type="button"
                        onClick={() => gameState.selectTile(heldSlice.id)}
                    >
                        Resume slice
                    </button>
                </div>
            )}

            {!isComplete && !heldSlice && selectedTile?.status === 'OPEN' && (
                <div className="atelier-claim">
                    <div className="atelier-claim-copy">
                        <div className="atelier-claim-kicker">Suggested plane</div>
                        <div className="atelier-claim-plane">{selectedLabel}</div>
                    </div>
                    <button
                        className="atelier-btn-primary atelier-claim-btn"
                        type="button"
                        onClick={() => handleClaimWithEnergy(selectedTile.id)}
                    >
                        Play
                        <span className="atelier-btn-cost" aria-label="Costs 1 key">
                            <IconKey className="atelier-btn-cost-ico" />
                            <span>−1</span>
                        </span>
                    </button>
                </div>
            )}

            <BlindGuessPanel
                model={model}
                category={model.category}
                completionPct={pct}
                disabled={isComplete}
                date={date}
                onFeedback={onFeedback}
            />

            <section className="atelier-workbenches">
                <div className="atelier-wb-head">
                    <h3>Atelier workbenches</h3>
                    <div className="atelier-filters" role="group" aria-label="Slice filter">
                        <button
                            type="button"
                            className={`atelier-filter${sliceFilter === 'ALL' ? ' is-active' : ''}`}
                            onClick={() => setSliceFilter('ALL')}
                        >
                            All
                        </button>
                        <button
                            type="button"
                            className={`atelier-filter${sliceFilter === 'OPEN' ? ' is-active' : ''}`}
                            onClick={() => setSliceFilter('OPEN')}
                        >
                            Open
                        </button>
                    </div>
                </div>

                <SliceGuideCube
                    size={model?.size || 16}
                    focus={sliceFocus}
                    solved={solvedPlanes}
                    offeredCount={tiles.length}
                    height={168}
                />
                <div className="atelier-axis-legend">
                    <span className="ax ax-x">X</span>
                    <span className="ax ax-y">Y</span>
                    <span className="ax ax-z">Z</span>
                </div>

                <div
                    className="vault-grid atelier-vault-grid"
                    onPointerLeave={() => {
                        if (selectedTile?.data?.axis != null) {
                            setSliceFocus({
                                axis: selectedTile.data.axis,
                                index: selectedTile.data.index,
                            });
                        }
                    }}
                >
                    {visibleTiles.map((tile) => (
                        <TileNode
                            key={tile.id}
                            tile={tile}
                            selected={tile.id === selectedTileId}
                            onSelectSlice={handleSelectSlice}
                            onHoverSlice={(meta) =>
                                setSliceFocus({ axis: meta.axis, index: meta.index })
                            }
                        />
                    ))}
                </div>
            </section>

            <nav className="atelier-dock" aria-label="Hub actions">
                <button
                    type="button"
                    className={`atelier-dock-item${!showCollection && !showLeaderboard ? ' is-active' : ''}`}
                    onClick={() => {
                        setShowCollection(false);
                        setShowLeaderboard(false);
                    }}
                >
                    <IconHub className="atelier-dock-ico" />
                    Hub
                </button>
                <button
                    type="button"
                    className={`atelier-dock-item${showCollection ? ' is-active' : ''}`}
                    onClick={() => {
                        setShowCollection(true);
                        setShowLeaderboard(false);
                    }}
                >
                    <IconMuseum className="atelier-dock-ico" />
                    Collection
                </button>
                <button
                    type="button"
                    className="atelier-dock-item"
                    onClick={() => {
                        setShowLeaderboard(false);
                        setShowCollection(false);
                        onOpenLobby?.();
                    }}
                >
                    <IconSquad className="atelier-dock-ico" />
                    Squad
                </button>
            </nav>

            {showCollection && <CollectionView onClose={() => setShowCollection(false)} />}
            {showLeaderboard && <Leaderboard onClose={() => setShowLeaderboard(false)} />}

            {showUnlockModal && (
                <div
                    className="atelier-bootcamp-graduate"
                    role="dialog"
                    aria-label="Atelier unlocked"
                >
                    <div className="atelier-bootcamp-graduate-card">
                        <div className="atelier-reveal-pill">● ATELIER UNLOCKED</div>
                        <h1 className="atelier-reveal-title">Denser carves await</h1>
                        <p className="atelier-bootcamp-graduate-copy">
                            Atelier unlocked — denser carves, richer finds. Workshop stays
                            available anytime.
                        </p>
                        <button
                            type="button"
                            className="atelier-btn-primary atelier-bootcamp-cta"
                            onClick={() => {
                                setShowUnlockModal(false);
                                handleTierSwitch(MYSTERY_TIER.ATELIER);
                            }}
                        >
                            Enter Atelier
                        </button>
                        <button
                            type="button"
                            className="atelier-bootcamp-cta-link"
                            onClick={() => setShowUnlockModal(false)}
                        >
                            Stay on Workshop
                        </button>
                    </div>
                </div>
            )}

            {isComplete && !hasDismissed && (
                <RevealCeremony
                    model={model}
                    hologram={hologram}
                    slicesSolved={solvedCount}
                    onDismiss={() => setHasDismissed(true)}
                />
            )}
        </div>
    );
};

export default MysteryHub;
